import { Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AdvancedFiltersDialog } from './AdvancedFiltersDialog'
import { useDebouncedSearch } from './useDebouncedSearch'
import { chipClass } from './chip'
import type {
  ActiveChip,
  FilterSection,
  FilterValues,
  QuickFilter,
} from './types'

interface FilterToolbarProps {
  searchPlaceholder: string
  /** Termo já aplicado (props da página), não o que está sendo digitado. */
  searchTerm: string
  onSearch: (term: string) => void

  sections: readonly FilterSection[]
  quickFilters?: readonly QuickFilter[]
  values: FilterValues
  onApplyFilters: (values: FilterValues) => void
  dialogDescription?: string
  extraKeys?: readonly string[]
  dialogExtras?: (
    values: FilterValues,
    setValue: (key: string, value: string) => void,
  ) => React.ReactNode

  /** Filtros ligados, já traduzidos para rótulo legível. */
  activeChips: readonly ActiveChip[]
  onRemoveFilter: (key: string) => void
  onClearAll: () => void
}

/**
 * Barra de busca + filtros das telas de lista.
 *
 * Duas decisões que vieram de ver a tela sendo usada:
 *
 * - os atalhos ficam na barra, não só dentro do dialog. São os recortes que a escola pede
 *   toda semana; estavam a dois cliques de distância, atrás de um botão que não dizia o
 *   que tinha dentro.
 * - os filtros ligados viram chips removíveis. Antes só existia o número no badge de
 *   "Filtros avançados": para descobrir o que estava filtrando era preciso abrir o dialog,
 *   e para tirar um só era preciso reaplicar todos.
 */
export function FilterToolbar({
  searchPlaceholder,
  searchTerm,
  onSearch,
  sections,
  quickFilters = [],
  values,
  onApplyFilters,
  dialogDescription,
  extraKeys,
  dialogExtras,
  activeChips,
  onRemoveFilter,
  onClearAll,
}: FilterToolbarProps) {
  const [term, setTerm] = useDebouncedSearch(searchTerm, onSearch)

  const isQuickActive = (quick: QuickFilter) =>
    Object.entries(quick.values).every(([key, value]) => values[key] === value)

  const toggleQuick = (quick: QuickFilter) => {
    const turningOff = isQuickActive(quick)
    const next = { ...values }
    Object.entries(quick.values).forEach(([key, value]) => {
      next[key] = turningOff ? '' : value
    })
    onApplyFilters(next)
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap items-center">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            onSearch(term)
          }}
          className="flex gap-2 flex-1 min-w-[240px]"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder={searchPlaceholder}
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              className="pl-10 pr-10"
            />
            {term && (
              <button
                type="button"
                aria-label="Limpar busca"
                onClick={() => {
                  setTerm('')
                  onSearch('')
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-80 transition-opacity"
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>
          <Button type="submit">Buscar</Button>
        </form>

        <AdvancedFiltersDialog
          sections={sections}
          quickFilters={quickFilters}
          values={values}
          onApply={onApplyFilters}
          description={dialogDescription}
          extraKeys={extraKeys}
        >
          {dialogExtras}
        </AdvancedFiltersDialog>
      </div>

      {quickFilters.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {quickFilters.map((quick) => {
            const Icon = quick.icon
            const selected = isQuickActive(quick)
            return (
              <button
                key={quick.label}
                type="button"
                aria-pressed={selected}
                onClick={() => toggleQuick(quick)}
                className={chipClass(selected)}
              >
                <Icon className="h-4 w-4" />
                {quick.label}
              </button>
            )
          })}
        </div>
      )}

      {activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-t pt-3">
          <span className="text-xs font-medium text-muted-foreground">
            Filtros ativos:
          </span>
          {activeChips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs text-secondary-foreground"
            >
              {chip.label}
              <button
                type="button"
                aria-label={`Remover filtro ${chip.label}`}
                onClick={() => onRemoveFilter(chip.key)}
                className="hover:opacity-70"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={onClearAll}
          >
            Limpar tudo
          </Button>
        </div>
      )}
    </div>
  )
}
